// Serien-Verwaltung Dialog (08/2026) — wird aus Kundendetails (Tab
// "Terminserien") UND aus dem Kalender-Kontextmenü ("Serie verwalten")
// geöffnet. Kann Uhrzeit + Trainer aller zukünftigen Termine ändern und
// die Serie beenden (nutzt bestehenden DELETE ?delete_scope=future).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Repeat, User, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('de-CH', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '–';

export default function SeriesManageDialog({ open, onOpenChange, seriesId, onChanged }) {
  const [summary, setSummary] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ending, setEnding] = useState(false);
  const [newTime, setNewTime] = useState('');
  const [newTrainerId, setNewTrainerId] = useState('');
  const [participantSearch, setParticipantSearch] = useState('');
  const [participantResults, setParticipantResults] = useState([]);

  const searchCustomers = async (q) => {
    setParticipantSearch(q);
    if (!q || q.length < 2) { setParticipantResults([]); return; }
    try {
      const res = await axios.get(`${API}/customers?search=${encodeURIComponent(q)}`, { headers: authHeaders() });
      setParticipantResults((res.data || []).slice(0, 6));
    } catch { setParticipantResults([]); }
  };

  const addParticipant = async (c) => {
    setParticipantSearch('');
    setParticipantResults([]);
    try {
      const res = await axios.post(
        `${API}/appointments/series/${seriesId}/add-participant`,
        { customer_id: c.id },
        { headers: authHeaders() }
      );
      const d = res.data;
      toast.success(`${d.customer_name} zu ${d.added} Terminen hinzugefügt` +
        (d.skipped_full > 0 ? ` (${d.skipped_full} übersprungen: voll)` : '') +
        (d.skipped_billed > 0 ? ` (${d.skipped_billed} bereits abgerechnet)` : ''));
      try {
        const s = await axios.get(`${API}/appointments/series/${seriesId}/summary`, { headers: authHeaders() });
        setSummary(s.data);
      } catch { /* Anzeige aktualisiert sich beim nächsten Öffnen */ }
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Hinzufügen fehlgeschlagen');
    }
  };

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  useEffect(() => {
    if (!open || !seriesId) return;
    setSummary(null);
    setNewTime('');
    setNewTrainerId('');
    setLoading(true);
    Promise.all([
      axios.get(`${API}/appointments/series/${seriesId}/summary`, { headers: authHeaders() }),
      axios.get(`${API}/trainers/availability/all`, { headers: authHeaders() }),
    ])
      .then(([sRes, tRes]) => {
        setSummary(sRes.data);
        setNewTime(sRes.data?.time_hhmm || '');
        setNewTrainerId(sRes.data?.trainer_id || '');
        const list = Array.isArray(tRes.data) ? tRes.data : tRes.data?.trainers || [];
        setTrainers(list);
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || 'Serie konnte nicht geladen werden');
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seriesId]);

  const timeChanged = summary && newTime && newTime !== summary.time_hhmm;
  const trainerChanged = summary && newTrainerId && newTrainerId !== summary.trainer_id;
  const hasChanges = timeChanged || trainerChanged;

  const handleSave = async () => {
    if (!hasChanges) return;
    if (
      !window.confirm(
        `Änderungen auf alle ${summary.upcoming_count} zukünftigen Termine der Serie anwenden?\n\n` +
          (timeChanged ? `• Neue Uhrzeit: ${newTime}\n` : '') +
          (trainerChanged ? '• Neuer Trainer wird zugewiesen\n' : '') +
          '\nHinweis: Kunden werden dabei NICHT automatisch benachrichtigt.'
      )
    )
      return;
    setSaving(true);
    try {
      const body = {};
      if (timeChanged) body.new_time = newTime;
      if (trainerChanged) body.trainer_id = newTrainerId;
      const res = await axios.put(
        `${API}/appointments/series/${seriesId}/future`,
        body,
        { headers: authHeaders() }
      );
      const r = res.data || {};
      if ((r.skipped_conflicts?.length || 0) === 0 && (r.billed_locked || 0) === 0) {
        toast.success(`${r.updated} Termine der Serie aktualisiert.`);
      } else {
        toast.warning(
          `${r.updated} Termine aktualisiert — ${r.skipped_conflicts?.length || 0} Konflikte übersprungen` +
            ((r.billed_locked || 0) > 0 ? `, ${r.billed_locked} bereits abgerechnet` : '') +
            '. Details siehe Konsole.'
        );
        if (r.skipped_conflicts?.length) console.warn('Serien-Update Konflikte:', r.skipped_conflicts);
      }
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Serien-Update fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleEndSeries = async () => {
    if (!summary?.first_upcoming_id) {
      toast.info('Keine zukünftigen Termine — Serie ist bereits beendet.');
      return;
    }
    if (
      !window.confirm(
        `Serie beenden?\n\nAlle ${summary.upcoming_count} zukünftigen Termine ab heute werden storniert ` +
          '(inkl. Mindbody-Bereinigung und Guthaben-Rückerstattung). Vergangene Termine bleiben erhalten.'
      )
    )
      return;
    if (
      !window.confirm(
        `Bist du WIRKLICH sicher?\n\n${summary.upcoming_count} zukünftige Termine dieser Serie werden ` +
          'unwiderruflich storniert. Dies kann NICHT rückgängig gemacht werden.'
      )
    )
      return;
    setEnding(true);
    try {
      const res = await axios.delete(
        `${API}/appointments/${summary.first_upcoming_id}?delete_scope=future&cancelled_by=trainer`,
        { headers: authHeaders() }
      );
      if (res.data?.series_id) {
        const { addSeriesJob } = await import('@/lib/seriesJobsStore');
        addSeriesJob(res.data.series_id);
      }
      toast.success('Serie wird im Hintergrund gelöscht — du kannst weiterarbeiten.');
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Serie konnte nicht beendet werden');
    } finally {
      setEnding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="series-manage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Repeat size={18} /> Serie verwalten
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-sm text-gray-500">Lade Serie…</div>}

        {!loading && summary && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-gray-50 p-3 space-y-1 text-sm" data-testid="series-summary-box">
              <div className="font-semibold text-base">{summary.service_name}</div>
              {summary.customer_names?.length > 0 && (
                <div className="flex items-center gap-1.5 text-gray-700">
                  <User size={14} /> {summary.customer_names.join(', ')}
                </div>
              )}
              <div className="flex items-center gap-1.5 text-gray-700">
                <Repeat size={14} /> {summary.frequency_label}
                {summary.weekday ? ` · ${summary.weekday}s um ${summary.time_hhmm}` : ''}
              </div>
              <div className="flex items-center gap-1.5 text-gray-700">
                <CalendarClock size={14} /> Nächster: {fmtDate(summary.next_start)} · {summary.upcoming_count} zukünftige / {summary.total} gesamt
              </div>
              {summary.trainer_name && (
                <div className="text-gray-600">Trainer: {summary.trainer_name}</div>
              )}
              {summary.billed_locked_count > 0 && (
                <div className="text-amber-700 text-xs">
                  {summary.billed_locked_count} Termin(e) bereits abgerechnet — bleiben unverändert.
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-sm">Uhrzeit (alle zukünftigen Termine)</Label>
                <Input
                  type="time"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  data-testid="series-edit-time-input"
                />
              </div>
              <div>
                <Label className="text-sm">Trainer</Label>
                <Select value={newTrainerId} onValueChange={setNewTrainerId}>
                  <SelectTrigger data-testid="series-edit-trainer-select">
                    <SelectValue placeholder="Trainer wählen…" />
                  </SelectTrigger>
                  <SelectContent>
                    {trainers.map((t) => {
                      const tr = t.trainer || t;
                      return (
                        <SelectItem key={tr.id} value={tr.id}>
                          {tr.first_name} {tr.last_name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Teilnehmer zur Serie hinzufügen</Label>
                <Input
                  placeholder="Kunde suchen…"
                  value={participantSearch}
                  onChange={(e) => searchCustomers(e.target.value)}
                  data-testid="series-add-participant-search"
                />
                {participantResults.length > 0 && (
                  <div className="mt-1 rounded-md border bg-white shadow-sm max-h-36 overflow-y-auto">
                    {participantResults.map((c) => (
                      <button
                        key={c.id}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100"
                        onClick={() => addParticipant(c)}
                        data-testid={`series-add-participant-${c.id}`}
                      >
                        {c.first_name} {c.last_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving || summary.upcoming_count === 0}
                data-testid="series-save-changes-btn"
              >
                {saving ? 'Speichere…' : `Änderungen auf ${summary.upcoming_count} zukünftige Termine anwenden`}
              </Button>
              <Button
                variant="destructive"
                onClick={handleEndSeries}
                disabled={ending || summary.upcoming_count === 0}
                data-testid="series-end-btn"
              >
                <XCircle size={16} className="mr-1" />
                {ending ? 'Beende…' : 'Serie beenden (alle zukünftigen stornieren)'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
